import { z } from "zod";

/**
 * The attachment manifest — the contract whose ABSENCE caused a prod outage.
 *
 * `apps/next-email-client` captures every inbound attachment, uploads it to the
 * `email-attachments` bucket, and builds a metadata array. It then reports the
 * message to Hub through a webhook payload that had nowhere to put that array,
 * so the variable was simply dropped at the call site. Result: 0 of 41,510
 * `communications` rows carry attachment metadata, and Meridian — told
 * `hasAttachments: true` by a different code path — spent ~40 tool calls
 * searching Drive and Slack for files that were sitting in Supabase Storage.
 *
 * Both apps now import this schema, so the field cannot be dropped silently
 * again: adding it to the producer without the consumer (or vice versa) is a
 * type error, not a runtime no-op.
 */

/** How a reader should treat an attachment, derived from its media type. */
export const ATTACHMENT_KINDS = [
  "image",
  "document",
  "calendar",
  "archive",
  "other",
] as const;

export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

const DOCUMENT_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
  "text/markdown",
]);

const ARCHIVE_TYPES = new Set([
  "application/zip",
  "application/x-zip-compressed",
  "application/x-tar",
  "application/gzip",
]);

/**
 * Classify by media type. Callers that have the BYTES should sniff them first
 * and pass the sniffed type — a declared `application/pdf` that is really an
 * HTML sign-in page is a real shape in this corpus.
 */
export function classifyAttachment(contentType: string): AttachmentKind {
  const type = contentType.toLowerCase().split(";")[0]?.trim() ?? "";
  if (type.startsWith("image/")) return "image";
  if (type === "text/calendar" || type === "application/ics") return "calendar";
  if (DOCUMENT_TYPES.has(type)) return "document";
  if (ARCHIVE_TYPES.has(type)) return "archive";
  return "other";
}

/**
 * Cap chosen against the live corpus: the largest observed attachment count on
 * a single message is well under this, and an unbounded array on a webhook is a
 * denial-of-service surface.
 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 50;

export const emailAttachmentSchema = z.object({
  filename: z.string().min(1).max(512),
  contentType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative().optional(),
  /**
   * Where the bytes live, in the producer's own storage namespace. NEVER a URL
   * and never something a model may construct — a consumer resolves it
   * server-side against the owning message before reading a byte.
   */
  storageKey: z.string().min(1).max(1024).optional(),
  /** True for embedded images (signature logos), which are noise by default. */
  inline: z.boolean().optional(),
});

export type EmailAttachment = z.infer<typeof emailAttachmentSchema>;

export const emailAttachmentManifestSchema = z
  .array(emailAttachmentSchema)
  .max(MAX_ATTACHMENTS_PER_MESSAGE);

export type EmailAttachmentManifest = z.infer<typeof emailAttachmentManifestSchema>;

/**
 * `null` and `[]` mean different things and the distinction is load-bearing:
 *   null -> never resolved; ask the provider
 *   []   -> resolved, genuinely none
 * Collapsing them makes an unvisited message indistinguishable from an
 * attachment-free one, which defeats any lazy-fill strategy.
 */
export type AttachmentManifestState = EmailAttachmentManifest | null;

export function isManifestResolved(
  state: AttachmentManifestState
): state is EmailAttachmentManifest {
  return state !== null;
}

/**
 * Did a person deliberately ATTACH this file, as opposed to the mail client
 * embedding it in the body?
 *
 * This is the distinction between "here is the signed PDF you asked for" and a
 * signature logo, a tracking pixel, or a quoted inline screenshot. Both are
 * attachments in MIME terms; only the first is a customer document. Every
 * producer already records which is which — Gmail from `Content-ID` /
 * `Content-Disposition: inline`, Graph from its native `isInline`, and the SES
 * lane from mailparser's `contentDisposition` / `cid` — so this is reading a
 * fact, never guessing from file size or filename.
 *
 * It lives in the contract package rather than in a consumer because the
 * `inline` flag's MEANING belongs with the flag. The alternative — each consumer
 * writing its own `!a.inline` — is how one surface ends up filing signature
 * logos into a customer's Drive folder while another does not, with nothing in
 * either place saying they were supposed to agree.
 *
 * **Absent means explicit.** A producer that never set the flag (an older row,
 * a lane that has not been taught to record it) yields `true`, so a document is
 * never silently dropped on a technicality. The cost of that default is a stray
 * logo; the cost of the opposite is a contract nobody can find.
 */
export function isExplicitAttachment(attachment: EmailAttachment): boolean {
  return attachment.inline !== true;
}

/**
 * The explicitly-attached files from a manifest, in order.
 *
 * The companion to {@link isExplicitAttachment} for the common case, so callers
 * filter through one named rule instead of an inline predicate that reads as an
 * implementation detail.
 */
export function selectExplicitAttachments(
  manifest: readonly EmailAttachment[],
): EmailAttachment[] {
  return manifest.filter((attachment) => isExplicitAttachment(attachment));
}

/**
 * How a manifest splits, for a caller that must REPORT what it skipped.
 *
 * Silently dropping the embedded images is right for filing but wrong for
 * telling someone what happened: "2 of 5 saved" reads like three failures
 * unless the other three are named as embedded images.
 */
export interface AttachmentSelection {
  explicit: EmailAttachment[];
  inline: EmailAttachment[];
}

export function splitAttachmentsByDisposition(
  manifest: readonly EmailAttachment[],
): AttachmentSelection {
  const explicit: EmailAttachment[] = [];
  const inline: EmailAttachment[] = [];
  for (const attachment of manifest) {
    if (isExplicitAttachment(attachment)) explicit.push(attachment);
    else inline.push(attachment);
  }
  return { explicit, inline };
}
